import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { IoArrowBack } from "react-icons/io5";
import { Skeleton } from "@mui/material";

const PROJECT_STATUSES = {
  planning: { label: "Planning", color: "bg-gray-100 text-gray-800" },
  active: { label: "Active", color: "bg-green-100 text-green-800" },
  on_hold: { label: "On Hold", color: "bg-yellow-100 text-yellow-800" },
  completed: { label: "Completed", color: "bg-blue-100 text-blue-800" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800" },
};

const ViewProject = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const { id } = useParams();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/getProject/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setProject(data.project);
      } else {
        setErrorMessage(data.error || "Failed to fetch project");
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch (err) {
      setErrorMessage("Failed to fetch project");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = PROJECT_STATUSES[status] || {
      label: status,
      color: "bg-gray-100 text-gray-800",
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6">
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6 text-center">
        <p className="text-gray-500">Project not found</p>
        <button
          onClick={() => navigate("/Projects")}
          className="mt-4 text-primary hover:underline"
        >
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/Projects")}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <IoArrowBack className="text-xl text-primary" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">{project.name}</h2>
            <p className="text-sm text-gray-500">
              {project.projectCode || "Project Details"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(project.status)}
        </div>
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Information */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
            Project Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Project Name</p>
              <p className="font-medium">{project.name}</p>
            </div>
            <div>
              <p className="text-gray-500">Project Code</p>
              <p className="font-medium text-primary">
                {project.projectCode || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Status</p>
              <div className="mt-1">{getStatusBadge(project.status)}</div>
            </div>
            <div>
              <p className="text-gray-500">Budget</p>
              <p className="font-medium">
                {project.budget ? `Nu. ${project.budget.toLocaleString()}` : "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
            Schedule
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Start Date</p>
              <p className="font-medium">{formatDate(project.startDate)}</p>
            </div>
            <div>
              <p className="text-gray-500">End Date</p>
              <p className="font-medium">{formatDate(project.endDate)}</p>
            </div>
            <div>
              <p className="text-gray-500">Duration</p>
              <p className="font-medium">
                {project.startDate && project.endDate
                  ? `${Math.ceil(
                      (new Date(project.endDate) - new Date(project.startDate)) /
                        (1000 * 60 * 60 * 24)
                    )} days`
                  : "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Team */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
            Team
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Department</p>
              <p className="font-medium">
                {project.department?.deptName || project.department?.name || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Manager</p>
              <p className="font-medium">
                {project.manager?.name ||
                  (project.manager?.firstName
                    ? `${project.manager.firstName} ${project.manager.lastName || ""}`.trim()
                    : "N/A")}
              </p>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
            Description
          </h3>
          <p className="text-sm text-gray-700">
            {project.description || "No description provided"}
          </p>
        </div>

        {/* Record Info */}
        <div className="bg-white rounded-lg shadow p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
            Record Info
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Created</p>
              <p className="font-medium">{formatDate(project.createdAt)}</p>
            </div>
            <div>
              <p className="text-gray-500">Last Updated</p>
              <p className="font-medium">{formatDate(project.updatedAt)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewProject;
